import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Mail, Phone, Building2, DollarSign, 
  Clock, CheckCircle, MessageSquare, RefreshCw, Search,
  Filter, Eye, Send, Archive, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvestorInquiries = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    converted: 0,
    archived: 0
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (user === null) return;
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchInquiries();
  }, [token, user, navigate]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/investor-inquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setInquiries(data.inquiries || []);
        
        // Calculate stats
        const all = data.inquiries || [];
        setStats({
          total: all.length,
          new: all.filter(i => i.status === 'new').length,
          contacted: all.filter(i => i.status === 'contacted').length,
          converted: all.filter(i => i.status === 'converted').length,
          archived: all.filter(i => i.status === 'archived').length
        });
      }
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const response = await fetch(
        `${API}/admin/investor-inquiries/${inquiryId}/status?status=${newStatus}&notes=${encodeURIComponent(notes)}`,
        {
          method: 'PUT',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchInquiries();
        setShowDetailModal(false);
        setNotes('');
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getFilteredInquiries = () => {
    let filtered = inquiries;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(i => i.status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i =>
        (i.name || '').toLowerCase().includes(term) ||
        (i.email || '').toLowerCase().includes(term) ||
        (i.company || '').toLowerCase().includes(term) ||
        (i.inquiry_id || '').toLowerCase().includes(term)
      );
    }
    
    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const getStatusBadge = (status) => {
    const styles = {
      'new': 'bg-blue-500/20 text-blue-400',
      'contacted': 'bg-yellow-500/20 text-yellow-400',
      'converted': 'bg-green-500/20 text-green-400',
      'archived': 'bg-gray-500/20 text-gray-400'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
        {(status || 'new').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInvestmentRangeColor = (range) => {
    if (!range) return 'text-gray-400';
    if (range.includes('$1M') || range.includes('$5M') || range.includes('$10M') || range.includes('$25M')) {
      return 'text-green-400 font-semibold';
    }
    if (range.includes('$500K') || range.includes('$250K')) {
      return 'text-yellow-400';
    }
    return 'text-gray-300';
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#b8860b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading investor inquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="admin-investor-inquiries">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard">
              <Button variant="ghost" size="sm" className="text-[#b8860b] hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                ADMIN
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-white text-lg font-light tracking-wide flex items-center gap-2">
                <Users className="w-5 h-5 text-[#b8860b]" />
                Investor Inquiries
              </h1>
              <p className="text-xs text-gray-500">Manage leads and investor interest</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchInquiries} data-testid="refresh-inquiries-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8" data-testid="inquiry-stats">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Inquiries</p>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">New</p>
                  <p className="text-2xl font-bold text-blue-400">{stats.new}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Contacted</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.contacted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Converted</p>
                  <p className="text-2xl font-bold text-green-400">{stats.converted}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center">
                  <Archive className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Archived</p>
                  <p className="text-2xl font-bold text-gray-400">{stats.archived}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a] mb-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Status Filter */}
              <div className="flex gap-2">
                {['all', 'new', 'contacted', 'converted', 'archived'].map((status) => (
                  <Button
                    key={status}
                    variant={statusFilter === status ? 'default' : 'outline'}
                    onClick={() => setStatusFilter(status)}
                    className={statusFilter === status ? 'bg-[#b8860b]' : 'border-[#1a2d4a] text-gray-400'}
                    size="sm"
                    data-testid={`filter-${status}`}
                  >
                    {status === 'all' ? `All (${stats.total})` : 
                     status === 'new' ? `New (${stats.new})` :
                     status === 'contacted' ? `Contacted (${stats.contacted})` :
                     status === 'converted' ? `Converted (${stats.converted})` :
                     `Archived (${stats.archived})`}
                  </Button>
                ))}
              </div>

              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, company..."
                    className="pl-10 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="search-inquiries"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inquiries List */}
        <Card className="bg-[#0d1d35] border-[#1a2d4a]">
          <CardHeader>
            <CardTitle className="text-white">Investor Inquiries</CardTitle>
            <CardDescription>
              {getFilteredInquiries().length} inquiry(ies) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {getFilteredInquiries().length > 0 ? (
              <div className="space-y-4" data-testid="inquiries-list">
                {getFilteredInquiries().map((inquiry) => (
                  <div 
                    key={inquiry.inquiry_id} 
                    className="p-4 rounded-lg border bg-[#0a1628]/50 border-[#1a2d4a] hover:border-[#b8860b]/50 transition-colors"
                    data-testid={`inquiry-${inquiry.inquiry_id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#b8860b]/20 flex items-center justify-center">
                          <span className="text-[#b8860b] font-bold">
                            {(inquiry.name || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{inquiry.name}</p>
                          <p className="text-xs text-gray-500 font-mono">{inquiry.inquiry_id}</p>
                        </div>
                      </div>
                      {getStatusBadge(inquiry.status)}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-gray-500 text-xs flex items-center gap-1">
                          <Mail className="w-3 h-3" /> Email
                        </p>
                        <p className="text-white truncate">{inquiry.email}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs flex items-center gap-1">
                          <Phone className="w-3 h-3" /> Phone
                        </p>
                        <p className="text-white">{inquiry.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> Company
                        </p>
                        <p className="text-white">{inquiry.company || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> Investment Range
                        </p>
                        <p className={getInvestmentRangeColor(inquiry.investment_range)}>
                          {inquiry.investment_range || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    {inquiry.message && (
                      <div className="mb-3 p-2 bg-[#1a2d4a]/50 rounded text-sm text-gray-300">
                        <p className="text-xs text-gray-500 mb-1">Message:</p>
                        <p className="line-clamp-2">{inquiry.message}</p>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-[#1a2d4a]">
                      <div className="text-xs text-gray-500">
                        <Clock className="w-3 h-3 inline mr-1" />
                        {formatDate(inquiry.created_at)}
                        {inquiry.contacted_at && (
                          <span className="ml-3 text-yellow-400">
                            Contacted: {formatDate(inquiry.contacted_at)}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="border-[#1a2d4a] text-gray-400"
                          onClick={() => {
                            setSelectedInquiry(inquiry);
                            setNotes(inquiry.notes || '');
                            setShowDetailModal(true);
                          }}
                          data-testid={`view-inquiry-${inquiry.inquiry_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        
                        {inquiry.status === 'new' && (
                          <Button 
                            size="sm" 
                            className="bg-yellow-600 hover:bg-yellow-700"
                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'contacted')}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" /> Mark Contacted
                          </Button>
                        )}
                        
                        {inquiry.status === 'contacted' && (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => updateInquiryStatus(inquiry.inquiry_id, 'converted')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Mark Converted
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>No investor inquiries found</p>
                <p className="text-sm mt-2">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'Inquiries from the /investors page will appear here'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1d35] border border-[#1a2d4a] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[#1a2d4a]">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-light text-white">{selectedInquiry.name}</h2>
                  <p className="text-xs text-gray-500 font-mono">{selectedInquiry.inquiry_id}</p>
                </div>
                {getStatusBadge(selectedInquiry.status)}
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-white">{selectedInquiry.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-white">{selectedInquiry.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Company</p>
                  <p className="text-white">{selectedInquiry.company || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Investment Range</p>
                  <p className={getInvestmentRangeColor(selectedInquiry.investment_range)}>
                    {selectedInquiry.investment_range || 'Not specified'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Accredited Investor</p>
                  <p className={selectedInquiry.accredited ? 'text-green-400' : 'text-red-400'}>
                    {selectedInquiry.accredited ? 'Yes' : 'No'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Submitted</p>
                  <p className="text-white">{formatDate(selectedInquiry.created_at)}</p>
                </div>
              </div>
              
              {selectedInquiry.message && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Message</p>
                  <div className="p-3 bg-[#0a1628] rounded border border-[#1a2d4a] text-gray-300">
                    {selectedInquiry.message}
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs text-gray-500 mb-2">Admin Notes</p>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this inquiry..."
                  className="w-full p-3 bg-[#0a1628] border border-[#1a2d4a] rounded text-white placeholder-gray-500 resize-none"
                  rows={3}
                  data-testid="inquiry-notes"
                />
              </div>
              
              <div className="flex gap-2 pt-4 border-t border-[#1a2d4a]">
                {selectedInquiry.status !== 'contacted' && (
                  <Button 
                    className="bg-yellow-600 hover:bg-yellow-700"
                    onClick={() => updateInquiryStatus(selectedInquiry.inquiry_id, 'contacted')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" /> Mark Contacted
                  </Button>
                )}
                {selectedInquiry.status !== 'converted' && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => updateInquiryStatus(selectedInquiry.inquiry_id, 'converted')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Mark Converted
                  </Button>
                )}
                {selectedInquiry.status !== 'archived' && (
                  <Button 
                    variant="outline"
                    className="border-gray-600 text-gray-400"
                    onClick={() => updateInquiryStatus(selectedInquiry.inquiry_id, 'archived')}
                  >
                    <Archive className="w-4 h-4 mr-2" /> Archive
                  </Button>
                )}
                <Button 
                  variant="ghost"
                  className="ml-auto"
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedInquiry(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvestorInquiries;
